import React from "react";
import PropTypes from "prop-types";

import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import ListSubheader from "@material-ui/core/ListSubheader";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";

/*
 * Select mit Suchfeld für Auswahllisten, die zu lang zum Scrollen sind (Hersteller: ~1000).
 * Material-UI 3.9.2 hat keine Autocomplete-Komponente, daher selbst gebaut.
 *
 * Zwei Dinge sind hier bewusst so gelöst und sollten nicht "vereinfacht" werden:
 *
 * 1) Das Suchfeld ist KEIN direktes Child des <Select>, sondern steckt in
 *    MenuProps.MenuListProps.subheader. SelectInput klont nämlich jedes Child und drückt ihm
 *    ein eigenes onClick plus value:undefined auf. Ein Klick ins Suchfeld würde das Menü
 *    schließen und onChange(undefined) auslösen. Der subheader geht an <List> durch und
 *    wird nicht geklont.
 *
 * 2) renderValue löst das Label über die UNGEFILTERTE labels-Map auf. Berechnet MUI die
 *    Anzeige aus den gerenderten Children, steht das Feld leer, sobald der gewählte
 *    Eintrag nicht in der gefilterten Trefferliste ist.
 */

const MAX_RESULTS = 50;

class SearchableSelect extends React.Component {
  constructor(props) {
    super(props);
    this.state = {open: false, search: ""};
    this.searchRef = null;
  }

  handleOpen = () => this.setState({open: true, search: ""});
  handleClose = () => this.setState({open: false, search: ""});

  focusSearch = () => {
    if (this.searchRef) { this.searchRef.focus() }
  };

  labelFor = (val) => {
    const {labels} = this.props;
    const key = String(val);
    return (labels && labels[key]) || key;
  };

  getFiltered() {
    const {options} = this.props;
    const q = (this.state.search || "").trim().toUpperCase();

    if (!q) {
      return {items: options.slice(0, MAX_RESULTS), total: options.length};
    }

    // Treffer am Wortanfang zuerst, das macht das 50er-Limit deutlich brauchbarer
    const starts = [];
    const contains = [];
    for (let i = 0; i < options.length; i++) {
      const pos = options[i].search.indexOf(q);
      if (pos === 0) { starts.push(options[i]) }
      else if (pos > 0) { contains.push(options[i]) }
    }
    const all = starts.concat(contains);
    return {items: all.slice(0, MAX_RESULTS), total: all.length};
  }

  choose(id) {
    this.setState({open: false, search: ""});
    this.props.onChangeValue(id);
  }

  handleSearchKeyDown = (event) => {
    const key = event.key;

    // Escape/Tab durchlassen, damit das Menü normal schließt
    if (key === "Escape" || key === "Tab") { return }

    if (key === "ArrowDown") {
      event.preventDefault();
      event.stopPropagation();
      const list = event.target.closest ? event.target.closest("ul") : null;
      if (list) {
        const opts = list.querySelectorAll('li[role="option"]:not([aria-disabled="true"])');
        if (opts.length > 0) { opts[0].focus() }
      }
      return;
    }

    if (key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      const filtered = this.getFiltered().items;
      if (this.state.search && filtered.length > 0) { this.choose(filtered[0].id) }
      return;
    }

    // Alles andere im Suchfeld behalten, sonst fängt MenuList Home/End/Pfeiltasten ab
    event.stopPropagation();
  };

  renderValue = (val) => {
    const {multiple, placeholder} = this.props;
    const empty = <em>{placeholder || "bitte wählen"}</em>;

    if (multiple) {
      const arr = Array.isArray(val) ? val : (val || val === 0 ? [val] : []);
      return arr.length ? arr.map(this.labelFor).join(", ") : empty;
    }
    if (val === "" || val === null || typeof val === "undefined") { return empty }
    return this.labelFor(val);
  };

  renderSearchBox() {
    return (
      <ListSubheader
        style={{
          // ListSubheader ist sticky, erbt aber transparenten Hintergrund
          // -> ohne das scrollen die Einträge sichtbar durch das Suchfeld
          backgroundColor: "#fff",
          lineHeight: "normal",
          paddingTop: 8,
          paddingBottom: 8
        }}
      >
        <TextField
          fullWidth
          autoComplete="off"
          placeholder={this.props.searchPlaceholder || "Suchen …"}
          inputRef={(ref) => { this.searchRef = ref }}
          value={this.state.search}
          onChange={(e) => this.setState({search: e.target.value})}
          onKeyDown={this.handleSearchKeyDown}
          onClick={(e) => e.stopPropagation()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
        />
      </ListSubheader>
    );
  }

  render() {
    const {
      id, name, label, value, multiple, required, disabled, error,
      className, style, placeholder, extra
    } = this.props;

    const {items, total} = this.getFiltered();
    const rest = total - items.length;

    // Auf Mobilgeräten soll die Bildschirmtastatur die Liste nicht komplett verdecken
    const maxHeight = Math.min(320, Math.round((window.innerHeight || 600) * 0.55));

    return (
      <FormControl style={style}>
        <InputLabel htmlFor={"input-" + id} shrink>{label}</InputLabel>
        <Select
          id={id}
          name={"sel" + name}
          className={className}
          displayEmpty
          native={false}
          open={this.state.open}
          onOpen={this.handleOpen}
          onClose={this.handleClose}
          value={value}
          multiple={multiple}
          required={required}
          disabled={disabled}
          error={error}
          renderValue={this.renderValue}
          inputProps={{name: "input" + name, id: "input-" + id}}
          onChange={(e) => this.props.onChangeValue(e.target.value)}
          MenuProps={{
            // sonst zieht Menu den Fokus auf das selektierte Item statt ins Suchfeld
            disableAutoFocusItem: true,
            onEntering: this.focusSearch,
            /*
             * Menu richtet sich normalerweise am selektierten Item aus und fällt ohne
             * ein solches auf das erste Listenkind zurück. Das ist hier das Suchfeld,
             * wodurch das Dropdown nach oben aus dem Bild wandert. Stattdessen wie ein
             * normales Dropdown unter dem Feld verankern.
             */
            getContentAnchorEl: null,
            anchorOrigin: {vertical: "bottom", horizontal: "left"},
            transformOrigin: {vertical: "top", horizontal: "left"},
            PaperProps: {style: {maxHeight: maxHeight, minWidth: 250, maxWidth: "92vw"}},
            MenuListProps: {subheader: this.renderSearchBox()}
          }}
        >
          <MenuItem value="" dense><em>{placeholder || "bitte wählen"}</em></MenuItem>

          {items.map((o) => (
            <MenuItem key={o.id} value={o.id} dense>{o.label}</MenuItem>
          ))}

          {items.length === 0 && (
            <MenuItem key="__none" dense disabled style={{whiteSpace: "normal", opacity: 0.7}}>
              {this.state.search
                ? "Keine Treffer"
                : (this.props.emptyHint || "Keine Auswahl verfügbar")}
            </MenuItem>
          )}

          {rest > 0 && (
            <MenuItem key="__more" dense disabled style={{whiteSpace: "normal", opacity: 0.7}}>
              {this.state.search
                ? rest + " weitere Treffer, bitte Suche verfeinern"
                : rest + " weitere Einträge, zum Suchen tippen"}
            </MenuItem>
          )}
        </Select>
        {extra}
      </FormControl>
    );
  }
}

SearchableSelect.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  // options: kategoriegefiltert, für die Liste. labels: ungefiltert, für die Anzeige.
  options: PropTypes.array.isRequired,
  labels: PropTypes.object.isRequired,
  value: PropTypes.any,
  multiple: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  placeholder: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  // Hinweis, wenn ohne Suchbegriff nichts zur Auswahl steht (z.B. Kategorie fehlt noch)
  emptyHint: PropTypes.string,
  extra: PropTypes.node,
  onChangeValue: PropTypes.func.isRequired
};

export default SearchableSelect;
